import { useEffect, useMemo, useRef } from 'react'
import { get, keyBy } from 'lodash'
import { DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM } from 'components/common/map/constants'
import { useFetchMetaFiltersQuery } from 'api/meta/getMetaFilters'
import { useFormContext, useWatch } from 'react-hook-form'
import {
  useLazyGetCustomerSchedulingAssignmentQuery,
  useLazyGetRoutesByDayQuery,
} from 'api/configured-service/configuredServiceSchedulingCrud'
import { formatDateToBEFormatDateFns } from 'utils/date'
import {
  setRoutesMetaDataForDay,
  selectAllRoutesMetaData,
  setRoutesLoading,
  setResultsMeta,
  clearResultsMeta,
} from 'slices/configuredService/configuredServiceScheduleSlice'
import { useDispatch, useSelector } from 'react-redux'
import { startOfWeek, endOfWeek, lastDayOfMonth } from 'date-fns'
import { orange } from '@mui/material/colors'
import { filterDefaultValues } from 'components/configured-service/CreateDraftConfiguredServiceDialog/filterSettings'
import { getScheduledDaysForService } from 'components/configured-service/tabs/MapRoutingTab/utils'
import { useDebounced } from 'components/configured-service/tabs/MapRoutingTab/hooks/useDebounced'

export const INITIAL_CAMERA = {
  center: DEFAULT_MAP_CENTER,
  zoom: DEFAULT_MAP_ZOOM,
}

export const useConfiguredServiceMapData = ({ latitude, longitude, surface, additionalDependencies = [] }) => {
  const [
    getSchedulingMapData,
    {
      isLoading: isLoadingSchedulingMapData,
      isFetching: isFetchingSchedulingMapData,
      isSuccess: isSuccessSchedulingMapData,
      data: rawData,
    },
  ] = useLazyGetCustomerSchedulingAssignmentQuery()
  const [
    getRoutesByDay,
    { isLoading: isLoadingRoutesByDay, isFetching: isFetchingRoutesByDay, isSuccess: isSuccessRoutesByDay, data: routesByDayData },
  ] = useLazyGetRoutesByDayQuery()
  const { control, getValues } = useFormContext()
  const dispatch = useDispatch()
  const routesMeta = useSelector(selectAllRoutesMetaData) || []
  // Normalize color field — routes-by-day API may return `routeColor` instead of `color`
  const routesMetaById = useMemo(
    () =>
      keyBy(
        routesMeta.map(r => ({ ...r, color: r.color || r.routeColor })),
        'id'
      ),
    [routesMeta]
  )

  const startDate = useWatch({ control, name: 'startDate', defaultValue: getValues('startDate') })
  const endDate = useWatch({ control, name: 'endDate', defaultValue: getValues('endDate') })
  const measure = useWatch({ control, name: 'measure', defaultValue: getValues('measure') })
  const material = useWatch({ control, name: 'material', defaultValue: getValues('material') })
  const action = useWatch({ control, name: 'action', defaultValue: getValues('action') })
  const businessLine = useWatch({ control, name: 'businessLine', defaultValue: getValues('businessLine') })
  const radius = useWatch({ control, name: 'radiusCommitted', defaultValue: getValues('radiusCommitted') })

  const formattedStartDate = useMemo(() => formatDateToBEFormatDateFns(startDate), [startDate])
  const formattedEndDate = useMemo(() => formatDateToBEFormatDateFns(endDate), [endDate])

  // Debounce filter values
  const debouncedMeasure = useDebounced(measure, 500)
  const debouncedMaterial = useDebounced(material, 500)
  const debouncedAction = useDebounced(action, 500)
  const debouncedBusinessLine = useDebounced(businessLine, 500)
  const debouncedRadius = useDebounced(radius, 500)
  const debouncedStartDateStr = useDebounced(formattedStartDate, 500)
  const debouncedEndDateStr = useDebounced(formattedEndDate, 500)
  const debouncedStartDate = useDebounced(startDate, 500)
  const debouncedEndDate = useDebounced(endDate, 500)

  const { data: metaFiltersData } = useFetchMetaFiltersQuery({})

  useEffect(() => {
    const effectiveRadius = debouncedRadius !== undefined ? debouncedRadius : filterDefaultValues.radius

    if (latitude && longitude && debouncedStartDateStr && debouncedEndDateStr) {
      const toArray = v => {
        if (Array.isArray(v)) return v
        return v ? [v] : []
      }
      const getList = (obj, keys) => {
        for (const key of keys) {
          const val = get(obj, key)
          if (val && val.length) return val
        }
        return []
      }

      const selectedMeasures = toArray(debouncedMeasure)
      const selectedMaterials = toArray(debouncedMaterial)
      const selectedActions = toArray(debouncedAction)
      const selectedBusinessLines = toArray(debouncedBusinessLine)

      const measureIds = (() => {
        const list = get(metaFiltersData, 'measures', getList(metaFiltersData, ['measure']))
        if (!list?.length || !selectedMeasures?.length) return undefined
        const ids = selectedMeasures
          .map(sel => {
            const { volume, unit } = sel || {}
            if (volume != null && unit != null) {
              const found = list.find(
                m => String(m.volume) === String(volume) && String(m.unit).toLowerCase() === String(unit).toLowerCase()
              )
              return found?.id
            }
            return null
          })
          .filter(Boolean)
        return ids.length ? ids.join(',') : undefined
      })()

      const resolveIds = (selectedItems, listKeys, matchKeys) => {
        const list = getList(metaFiltersData, listKeys)
        if (!list.length || !selectedItems.length) return undefined
        const norm = v => (v == null ? '' : String(v).trim().toLowerCase())

        const ids = selectedItems
          .map(sel => {
            const val = sel?.label ?? sel?.value
            const n = norm(val)
            const found = list.find(item => matchKeys.some(k => norm(item[k]) === n))
            return found?.id
          })
          .filter(Boolean)
        return ids.length ? ids.join(',') : undefined
      }

      const materialIds = resolveIds(selectedMaterials, ['materials', 'material'], ['materialType', 'materialName'])
      const actionIds = resolveIds(selectedActions, ['serviceActions', 'serviceAction'], ['actionType', 'actionName'])
      const businessLinesParam = resolveIds(
        selectedBusinessLines,
        ['businessLines', 'businessLineList'],
        ['businessLineLabel', 'businessLineName']
      )

      const requestParams = {
        latitude,
        longitude,
        radiusMeters: Math.max(0, Math.round(effectiveRadius || 0)),
        startDate: debouncedStartDateStr,
        endDate: debouncedEndDateStr,
        measureIds,
        materialIds,
        actionIds,
        businessLines: businessLinesParam,
      }

      getSchedulingMapData(requestParams, false)
      getRoutesByDay(requestParams, false)
    }
  }, [
    metaFiltersData,
    latitude,
    longitude,
    debouncedRadius,
    debouncedStartDateStr,
    debouncedEndDateStr,
    debouncedMeasure,
    debouncedMaterial,
    debouncedAction,
    debouncedBusinessLine,
    ...additionalDependencies,
  ])

  useEffect(() => {
    if (routesByDayData) {
      const routesByDay = get(routesByDayData, 'routesByDay', {})
      dispatch(setRoutesMetaDataForDay(routesByDay))
    }
  }, [routesByDayData, dispatch])

  useEffect(() => {
    dispatch(setRoutesLoading(isFetchingRoutesByDay || isFetchingSchedulingMapData))
  }, [isFetchingRoutesByDay, isFetchingSchedulingMapData, dispatch])

  useEffect(() => {
    dispatch(
      setResultsMeta({
        surface,
        truncated: get(rawData, 'truncated', false),
        totalMatched: get(rawData, 'totalMatched', 0),
        cap: get(rawData, 'cap', 0),
      })
    )
  }, [rawData, surface, dispatch])

  // Drop this surface's cap on unmount so it can't outlive the map that reported it.
  useEffect(
    () => () => {
      dispatch(clearResultsMeta(surface))
    },
    [surface, dispatch]
  )

  const prevDataRef = useRef([])
  const data = useMemo(() => {
    const rawServices = get(rawData, 'configuredServices', [])
    if (!rawServices.length) return []

    const refDate = debouncedStartDate || startOfWeek(new Date())
    const refEndDate = debouncedEndDate || endOfWeek(new Date())

    const context = {
      year: refDate.getFullYear(),
      monthIndex: refDate.getMonth(),
      lastDay: lastDayOfMonth(refDate),
      startTime: refDate.getTime(),
      endTime: refEndDate.getTime(),
    }

    const prevDataMap = new Map(prevDataRef.current.map(item => [item.id, item]))

    const newData = rawServices
      .filter(({ addressLatitude, addressLongitude }) => {
        return addressLatitude && addressLongitude
      })
      .map(service => {
        const latitude = parseFloat(get(service, 'addressLatitude'))
        const longitude = parseFloat(get(service, 'addressLongitude'))
        const weekDays = getScheduledDaysForService({
          service,
          startDate: refDate,
          endDate: refEndDate,
          context,
        })
        const serviceRoutes = get(service, 'routes', [])
        const hasRoutes = Array.isArray(serviceRoutes) && serviceRoutes.length > 0
        const firstRouteId = hasRoutes ? get(serviceRoutes, '[0].routeId') : undefined
        // resolveRouteColor: returns the route color from routesMetaById, falling
        // back to orange[300]. Explicit null from the DB is treated as absent —
        // lodash `get` only substitutes the default for `undefined`, not `null`.
        const resolveRouteColor = routeId => routesMetaById[routeId]?.color || orange[300]

        const backgroundColor = hasRoutes ? resolveRouteColor(firstRouteId) : orange[300]

        // Enrich routes with color from routesMetaById so WebGL route-mode markers
        // can read `route.color` without a separate lookup. Reuse the previous
        // enriched array when colors haven't changed to preserve reference stability.
        const prevItem = prevDataMap.get(service.id)
        const prevEnrichedRoutes = prevItem?.routes
        const enrichedRoutes = hasRoutes
          ? serviceRoutes.map((r, i) => {
              const color = resolveRouteColor(r.routeId)
              const prev = prevEnrichedRoutes?.[i]
              return prev && prev.routeId === r.routeId && prev.color === color ? prev : { ...r, color }
            })
          : serviceRoutes

        // Stabilize the routes array reference when all individual route objects
        // were reused from the previous render so the equality guard below works.
        const stableRoutes =
          prevEnrichedRoutes &&
          prevEnrichedRoutes.length === enrichedRoutes.length &&
          enrichedRoutes.every((r, i) => r === prevEnrichedRoutes[i])
            ? prevEnrichedRoutes
            : enrichedRoutes

        if (
          prevItem &&
          prevItem.latitude === latitude &&
          prevItem.longitude === longitude &&
          prevItem.weekDays === weekDays &&
          prevItem.backgroundColor === backgroundColor &&
          prevItem.routes === stableRoutes
        ) {
          return prevItem
        }

        return {
          ...service,
          routes: stableRoutes,
          weekDays,
          backgroundColor,
          latitude,
          longitude,
        }
      })

    prevDataRef.current = newData
    return newData
  }, [rawData, debouncedStartDate, debouncedEndDate, routesMetaById, debouncedStartDateStr, debouncedEndDateStr])

  return {
    data,
    rawData,
    totalMatched: get(rawData, 'totalMatched', 0),
    truncated: get(rawData, 'truncated', false),
    cap: get(rawData, 'cap', 0),
    isLoading: isLoadingSchedulingMapData || isLoadingRoutesByDay,
    isFetching: isFetchingSchedulingMapData || isFetchingRoutesByDay,
    isSuccess: isSuccessSchedulingMapData && isSuccessRoutesByDay,
  }
}
